import { useEffect, useMemo, useState } from "react";
import { T } from "../../../app/tokens.js";
import { tx } from "./travelStyles.js";

const DEFAULT_STAGES = new Set(["operational", "dormant", "build"]);
export function officeChoiceLabel(office) {
  // Presentation only. The destination snapshot continues to use office.label.
  return office.marketName ? `${office.marketName} · ${office.alias || office.label || office.id}`
    : office.label || office.id;
}
export function officeChoiceGroups(locations, query, showAll) {
  const q = query.trim().toLocaleLowerCase();
  const sorted = locations.filter(l => !l.deletedAt && l.available !== false &&
    (showAll || DEFAULT_STAGES.has(l.stage)) &&
    `${officeChoiceLabel(l)} ${l.region || ""}`.toLocaleLowerCase().includes(q))
    .sort((a, b) => (a.marketName || "").localeCompare(b.marketName || "") ||
      (a.alias || a.label || a.id).localeCompare(b.alias || b.label || b.id) || a.id.localeCompare(b.id));
  const groups = new Map();
  for (const office of sorted) {
    const region = office.region || "Region unavailable";
    if (!groups.has(region)) groups.set(region, []);
    groups.get(region).push(office);
  }
  return [...groups].sort(([a], [b]) => a.localeCompare(b));
}
export function TravelOfficePicker({ locations, selectedId, snapshotLabel, onSelect, resetKey }) {
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setQuery(""); setShowAll(false); }, [resetKey]);
  const groups = useMemo(() => officeChoiceGroups(locations, query, showAll), [locations, query, showAll]);
  const selected = locations.find(l => l.id === selectedId);
  const selectedVisible = groups.some(([, rows]) => rows.some(l => l.id === selectedId));
  return <div>
    <input type="search" aria-label="Search offices" data-testid="travel-office-search" style={{ ...tx.input, marginBottom: 6 }}
      value={query} onChange={e => setQuery(e.target.value)} placeholder="Search market or alias" />
    <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: T.fsSm, marginBottom: 6 }}>
      <input type="checkbox" data-testid="travel-office-show-all" checked={showAll} onChange={e => setShowAll(e.target.checked)} />Show all offices
    </label>
    <select id="tr-office" data-testid="travel-input-office" style={tx.input} value={selectedId} onChange={e => onSelect(e.target.value)}>
      <option value="">Choose an office…</option>
      {selectedId && !selectedVisible && <option value={selectedId}>
        {selected ? `${officeChoiceLabel(selected)} (current selection, outside results)` : `${snapshotLabel || selectedId} (unavailable office, historical selection)`}
      </option>}
      {groups.map(([region, rows]) => <optgroup key={region} label={region}>
        {rows.map(l => <option key={l.id} value={l.id}>{officeChoiceLabel(l)}{l.timeZone ? "" : " (time zone unavailable)"}</option>)}
      </optgroup>)}
    </select>
    {!groups.length && <div role="status" style={{ fontSize: T.fsXs, color: T.mid }}>No selectable offices match. Change the search or Show all offices.</div>}
  </div>;
}
